/**
 * Наблюдения заданий (E4.6c): страницы сообщают о действиях пользователя в учебном
 * экземпляре -- открыл ресурс на карте, событие, событие LLM-шлюза, сохранил разбор или
 * политику. Сообщение публикуют только обработчики действий пользователя и подтверждённые
 * сервером сохранения; само появление элемента, переход по адресу и оптимистичное
 * изменение наблюдениями не являются. Во время тура действие помечено как действие тура
 * и заданию не засчитывается.
 */

import { useCallback } from 'react';
import { useWorkspaceDataSource } from '@/features/workspace/WorkspaceDataSourceContext';
import { trainingInstanceOf } from './types';
import type { TourState } from './tourMachine';
import { useTourStore } from './tourStore';

export type ObservationKind =
	| 'resource-visible'
	| 'event-opened'
	| 'event-saved'
	| 'policy-saved'
	| 'llm-event-opened';

export interface TrainingObservation {
	source: 'user' | 'tour' | 'restore';
	instance_id: string;
	kind: ObservationKind;
	entity_id: string;
	version?: number;
}

type Listener = (observation: TrainingObservation) => void;

const listeners = new Set<Listener>();

export function observeTrainingAction(observation: TrainingObservation): void {
	listeners.forEach((listener) => listener(observation));
}

export function onTrainingObservation(listener: Listener): () => void {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
}

/** Источник действия на странице: идущий тур делает его действием тура */
export function observationSource(tour: TourState): TrainingObservation['source'] {
	return tour.phase === 'idle' || tour.phase === 'paused' ? 'user' : 'tour';
}

/** Сообщить о действии пользователя; в рабочем контуре -- ничего */
export function useTrainingAction(): (
	kind: ObservationKind,
	entityId: string,
	version?: number,
) => void {
	const { mode } = useWorkspaceDataSource();
	const instance = trainingInstanceOf(mode);
	return useCallback(
		(kind, entityId, version) => {
			if (instance === null) return;
			observeTrainingAction({
				source: observationSource(useTourStore.getState().tour),
				instance_id: instance,
				kind,
				entity_id: entityId,
				...(version === undefined ? {} : { version }),
			});
		},
		[instance],
	);
}
